import { Component, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-red-400" />
          <div>
            <p className="text-lg font-bold text-foreground">Something went wrong</p>
            <p className="text-sm text-red-400 mt-1 max-w-md font-mono">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            {this.state.error?.stack && (
              <pre className="mt-3 text-[10px] text-muted-foreground max-h-48 overflow-auto text-left whitespace-pre-wrap bg-white/5 p-3 rounded max-w-xl">
                {this.state.error.stack}
              </pre>
            )}
          </div>
          <button
            onClick={() => {
              this.handleReset()
              window.location.href = '/strategy'
            }}
            className="flex items-center gap-2 rounded-lg px-4 py-2 bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Back to Strategy
          </button>
        </div>
      )
    }
    return this.props.children
  }
}